import React, { useCallback, useState } from 'react';

import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import s from './QuickEditInventoryModal.module.css';
import {
    loadWarehouseListRequest,
    sendChangeInventoryStatusRequest,
    sendCopyInventoryRequest,
    sendMoveInventoryRequest,
} from '../../store/slices/boardSlice';
import {
    changeIsMoveInventoryQuantityModalOpened,
    setWarehouseFrom,
    setWarehouseTo,
} from '../../store/slices/moveInventoryQuantityModalSlice';
import { setNeedUpdateTotalInfo } from '../../store/slices/totalInfoSlice';
import { getWarehouseTotalInfoRequest } from '../../store/slices/warehouseTotalInfoSlice';
import {
    BrokenIcon,
    CopyIcon,
    DecommissionIcon,
    DeleteIcon,
    FreeIcon,
    InWorkIcon,
    TransferIcon,
} from '../EditInventoryModal/Status/StatusImage';
import { useInventoryStatusesData } from '../Inventory/inventoryStatusesData';
import { SearchModal } from '../SearchModal/SearchModal';

const QuickEditButton = ({ image, onClick, text, dataId }) => (
    <div data-id={dataId} className={s.button} onClick={onClick} key={text}>
        {image}
        <span className={s.buttonText}>{text}</span>
    </div>
);

export const QuickEditInventoryModal = ({
    status,
    inventory,
    warehouseId,
    hideOverlay,
    deleteInventory,
    isLeftModeModal,
}) => {
    const dispatch = useDispatch();
    const [isInventoryMove, setIsInventoryMove] = useState(false);
    const [buttonsData, setButtonsData] = useState([]);
    const warehouses = useSelector((state) => state.board);
    const hasFullAccess = useSelector((state) => state.restrictions);

    const { inventoryStatusesData } = useInventoryStatusesData();
    const statusButtonsData = inventoryStatusesData.filter((item) => item.status !== status);

    const onMoveInventory = useCallback(
        (warehouse) => {
            const warehouseFrom = warehouses.find((item) => item.id === warehouseId);
            const warehouseTo = warehouses.find((item) => item.id === warehouse.id);

            const newIndex = warehouses.find((item) => item.id === warehouse.id).items.length;
            const payload = {
                inventoryGuid: inventory.guid,
                fromWarehouseId: warehouseId,
                toWarehouseId: warehouse.id,
                newIndex,
                inventory,
            };

            if (inventory.amount > 1) {
                dispatch(setWarehouseFrom(warehouseFrom));
                dispatch(setWarehouseTo(warehouseTo));
                dispatch(
                    changeIsMoveInventoryQuantityModalOpened({
                        value: true,
                        inventory,
                        inventoryPosition: warehouseTo.items.length,
                    })
                );
            } else {
                dispatch(sendMoveInventoryRequest(payload));
            }
            hideOverlay();
        },
        [warehouses]
    );

    const loadWarehouseList = () => {
        const warehouseList = dispatch(loadWarehouseListRequest);
        warehouseList
            .then((response) => response.json())
            .then((data) => {
                setButtonsData(data.response.filter((item) => item.id !== warehouseId));
            });
    };
    const [isStatusDisable, setIsStatusDisable] = useState(false);

    const transferIcon = <TransferIcon className={s.buttonImage} />;
    const deleteIcon = <DeleteIcon className={s.buttonImage} />;
    const copyIcon = <CopyIcon className={s.buttonImage} />;

    const changeStatus = (changedStatus) => {
        setIsStatusDisable(true);
        const changeStatusServerResponse = dispatch(
            sendChangeInventoryStatusRequest({
                warehouseId,
                inventoryGuid: inventory.guid,
                status: changedStatus,
            })
        );
        changeStatusServerResponse
            .then((response) => response.json())
            .then(() => {
                setIsStatusDisable(false);
                dispatch(setNeedUpdateTotalInfo(true));
                dispatch(getWarehouseTotalInfoRequest(warehouseId));
            });
        hideOverlay();
    };

    return (
        <div className={cx(s.modal, { [s.leftMode]: isLeftModeModal })}>
            {!isInventoryMove && (
                <div>
                    {warehouses.length > 1 && (
                        <QuickEditButton
                            dataId="move-inventory"
                            key="move"
                            text={i18next.t('QuickEditInventoryModal.QuickEditButton.move')}
                            image={transferIcon}
                            onClick={() => {
                                setIsInventoryMove(true);
                                loadWarehouseList();
                            }}
                        />
                    )}
                    {hasFullAccess && (
                        <QuickEditButton
                            key="copy"
                            text={i18next.t('QuickEditInventoryModal.QuickEditButton.copy')}
                            data-id="copy-inventory"
                            image={copyIcon}
                            onClick={() => {
                                dispatch(
                                    sendCopyInventoryRequest({
                                        inventoryGuid: inventory.guid,
                                        warehouseId,
                                    })
                                );
                                hideOverlay();
                            }}
                        />
                    )}
                    {statusButtonsData.map((item) => {
                        let icon;

                        if (item.status === 0) {
                            icon = <InWorkIcon className={s.buttonImage} />;
                        }
                        if (item.status === 1) {
                            icon = <FreeIcon className={s.buttonImage} />;
                        }
                        if (item.status === 2) {
                            icon = <BrokenIcon className={s.buttonImage} />;
                        }
                        if (item.status === 3) {
                            icon = <DecommissionIcon className={s.buttonImage} />;
                        }

                        return (
                            <QuickEditButton
                                dataId={item.status}
                                key={item.text}
                                text={item.text}
                                image={icon}
                                status={status}
                                onClick={() => {
                                    if (!isStatusDisable) {
                                        changeStatus(item.status);
                                    }
                                }}
                            />
                        );
                    })}
                    {status === 3 && hasFullAccess && (
                        <QuickEditButton
                            dataId="remove-inventory"
                            key="remove"
                            text={i18next.t('QuickEditInventoryModal.QuickEditButton.remove')}
                            image={deleteIcon}
                            onClick={() => {
                                deleteInventory();
                            }}
                        />
                    )}
                </div>
            )}
            {isInventoryMove && (
                <SearchModal
                    titleText={i18next.t('QuickEditInventoryModal.SearchModal.titleText')}
                    buttonClick={onMoveInventory}
                    buttonsData={buttonsData}
                    hideOverlay={hideOverlay}
                />
            )}
        </div>
    );
};
